import React, { useState } from "react";
import type { McqPreviewModel } from "./questionPreviewAdapter";

type McqQuestionPreviewProps = {
  model: McqPreviewModel;
};

const optionAccentColor = "#2b5a9e";

/** Trainer preview matching student TestMcq content area; correct answer pre-selected & locked. */
const McqQuestionPreview: React.FC<McqQuestionPreviewProps> = ({ model }) => {
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(
    null
  );
  const selectedOption = model.correct_answer;
  const optionsLocked = true;

  return (
    <div
      className="overflow-auto h-100 d-flex flex-column p-0 px-3"
      style={{
        backgroundColor: "#f8f9fa",
        borderRadius: "10px",
        minHeight: "60vh",
      }}
    >
      <div
        className="fw-semibold flex-shrink-0 pb-2"
        style={{
          color: "#1a237e",
          fontSize: "1.1rem",
        }}
      >
        Question 1
      </div>
      <div
        className="mb-4 flex-shrink-0"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            color: "#212529",
            scrollbarWidth: "thin",
          }}
        >
          {model.question}
        </div>
      </div>

      <div className="flex-shrink-0">
        {model.options.map((option, index) => {
          const isSelected = selectedOption === option;
          const isHovered = hoveredOptionIndex === index;
          const letter = String.fromCharCode(65 + index);
          const highlightLetter = !optionsLocked && isHovered;
          const optionId = `mcq-preview-option-${index}`;

          return (
            <label
              key={`${index}-${option}`}
              htmlFor={undefined}
              onMouseEnter={() =>
                !optionsLocked && setHoveredOptionIndex(index)
              }
              onMouseLeave={() => setHoveredOptionIndex(null)}
              onClick={(e) => {
                if (optionsLocked) e.preventDefault();
              }}
              className="d-flex align-items-center mb-3"
              style={{
                backgroundColor: isSelected
                  ? "#ebf2ff"
                  : isHovered && !optionsLocked
                    ? "#f1f3f5"
                    : "#ffffff",
                border: isSelected
                  ? `2px solid ${optionAccentColor}`
                  : "1px solid #e9ecef",
                borderRadius: "10px",
                boxShadow:
                  isSelected || (isHovered && !optionsLocked)
                    ? "none"
                    : "0 2px 4px rgba(0,0,0,0.05)",
                padding: "14px 18px",
                cursor: optionsLocked ? "not-allowed" : "pointer",
                pointerEvents: optionsLocked ? "none" : "auto",
                transition: "border-color 0.15s, background-color 0.15s",
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: highlightLetter
                    ? optionAccentColor
                    : "#f1f3f5",
                  borderRadius: "6px",
                  color: highlightLetter ? "#ffffff" : "#495057",
                  fontWeight: 600,
                  marginRight: "14px",
                  transition: "background-color 0.15s, color 0.15s",
                }}
              >
                {letter}
              </div>
              <div
                className="flex-grow-1"
                style={{
                  whiteSpace: "pre-line",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  color: "#212529",
                }}
              >
                {option}
              </div>
              <input
                type="radio"
                id={optionId}
                name="mcq-preview"
                value={option}
                checked={isSelected}
                readOnly
                disabled={optionsLocked}
                className="flex-shrink-0 ms-3"
                style={{
                  width: "20px",
                  height: "20px",
                  accentColor: optionAccentColor,
                  cursor: optionsLocked ? "not-allowed" : "pointer",
                }}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default McqQuestionPreview;
